
import {
  AutomationIcon, ClientSupplierIcon, DashboardIcon,
  CalendarIcon, ClockIcon, CurrencyDollarIcon, DocumentTextIcon, EmployeesIcon, ExpensesIcon, IntegrationsIcon, InventoryIcon, InvoicesIcon,
  PaymentsIcon, ReportsAnalyticsIcon,
  SecurityComplianceIcon, SettingsIcon
} from '../../assets/icons';
import { NavLink, NavLinkWithoutIcon, NavLinkWithoutHrefNIcon, DashboardViewTabs, EPayrollProcessingSubTab } from '../../types';
import { FeatureFlag, FeatureFlagType, hasAnyFeature } from 'kmstack-shared/apps/accounting';
import { PlanType, RoleType } from 'kmstack-shared/core';


export const dashBoardEssentialsLinks = (iconClasses: string): NavLink[] => {
  return [
    { name: "Dashboard", icon: <DashboardIcon className={iconClasses} />, href: "", viewId: DashboardViewTabs.OVERVIEW },
    { name: "Invoices", icon: <InvoicesIcon className={iconClasses} />, href: "org/invoices", viewId: DashboardViewTabs.INVOICES },
    { name: "Clients & Suppliers", icon: <ClientSupplierIcon className={iconClasses} />, href: "org/clients-suppliers", viewId: DashboardViewTabs.CLIENTS_SUPPLIERS },
    { name: "Payments", icon: <PaymentsIcon className={iconClasses} />, href: "org/payments", viewId: DashboardViewTabs.PAYMENTS },
    { name: "Purchases", icon: <ExpensesIcon className={iconClasses} />, href: "org/purchases", viewId: DashboardViewTabs.PURCHASES },
  ]
}

export const employeePortalLinks = (iconClasses: string): NavLink[] => {
  return [
    { name: "Employee Portal", icon: <EmployeesIcon className={iconClasses} />, href: "org/employee-portal", viewId: DashboardViewTabs.EMPLOYEE_PORTAL },
  ]
}

export const dashBoardToolsLinks = (iconClasses: string): NavLink[] => {
  return [
    { name: "Inventory", icon: <InventoryIcon className={iconClasses} />, href: "org/inventory", viewId: DashboardViewTabs.INVENTORY },
    { name: "Reports & Analytics", icon: <ReportsAnalyticsIcon className={iconClasses} />, href: "org/report-analytics", viewId: DashboardViewTabs.REPORT_ANALYTICS },
    { name: "Automation", icon: <AutomationIcon className={iconClasses} />, href: "org/automation", viewId: DashboardViewTabs.AUTOMATION },
    { name: "Security & Compliance", icon: <SecurityComplianceIcon className={iconClasses} />, href: "org/security-compliance", viewId: DashboardViewTabs.SECURITY_COMPLIANCE },
    { name: "Integrations", icon: <IntegrationsIcon className={iconClasses} />, href: "org/integrations", viewId: DashboardViewTabs.INTEGRATIONS },
    { name: "Settings", icon: <SettingsIcon className={iconClasses} />, href: "org/settings", viewId: DashboardViewTabs.SETTINGS },
  ]
}

export const workforceSidebarLinks = (iconClasses: string): NavLink[] => {
  const timeTrackingPlans = [PlanType.STANDARD, PlanType.PROFESSIONAL, PlanType.ENTERPRISE];
  const peopleOpsPlans = [PlanType.PROFESSIONAL, PlanType.ENTERPRISE];

  return [
    { name: "Employees", icon: <EmployeesIcon className={iconClasses} />, href: "org/workforce/users", viewId: "users", parentViewId: DashboardViewTabs.WORKFORCE, visibleOnPlans: peopleOpsPlans },
    { name: "Time Tracking", icon: <ClockIcon className={iconClasses} />, href: "org/workforce/time-tracking/timer", viewId: "timer", parentViewId: DashboardViewTabs.WORKFORCE, visibleOnPlans: timeTrackingPlans },
    { name: "Leave", icon: <CalendarIcon className={iconClasses} />, href: "org/workforce/leave-approvals", viewId: "leave-approvals", parentViewId: DashboardViewTabs.WORKFORCE, visibleOnPlans: peopleOpsPlans },
    { name: "Projects", icon: <ClockIcon className={iconClasses} />, href: "org/workforce/time-tracking/projects", viewId: "projects", parentViewId: DashboardViewTabs.WORKFORCE, visibleOnPlans: timeTrackingPlans },
    { name: "Timesheets", icon: <DocumentTextIcon className={iconClasses} />, href: "org/workforce/time-tracking/time-sheets", viewId: "time-sheets", parentViewId: DashboardViewTabs.WORKFORCE, visibleOnPlans: timeTrackingPlans },
  ]
}

export const payrollSidebarLinks = (iconClasses: string): NavLink[] => {
  const payrollPlans = [PlanType.ENTERPRISE];

  return [
    { name: "Run Payroll", icon: <CurrencyDollarIcon className={iconClasses} />, href: "org/workforce/payroll-processing", viewId: "payroll-processing", parentViewId: DashboardViewTabs.WORKFORCE, visibleOnPlans: payrollPlans },
    { name: "Pay Slips", icon: <DocumentTextIcon className={iconClasses} />, href: "org/workforce/payslips", viewId: "payslips", parentViewId: DashboardViewTabs.WORKFORCE, visibleOnPlans: payrollPlans },
    { name: "Payroll Settings", icon: <SettingsIcon className={iconClasses} />, href: "org/workforce/payroll-settings", viewId: "payroll-settings", parentViewId: DashboardViewTabs.WORKFORCE, visibleOnPlans: payrollPlans },
  ]
}

export const dashboardViewFeatures: Partial<Record<DashboardViewTabs, readonly FeatureFlagType[]>> = {
  [DashboardViewTabs.OVERVIEW]: [FeatureFlag.KmstackDashboard],
  [DashboardViewTabs.INVOICES]: [FeatureFlag.KmstackABInvoice],
  [DashboardViewTabs.CLIENTS_SUPPLIERS]: [FeatureFlag.KmstackClientManagement, FeatureFlag.KmstackABClientPortal],
  [DashboardViewTabs.WORKFORCE]: [
    FeatureFlag.KmstackEmployeeManagement,
    FeatureFlag.KmstackABTimesheet,
    FeatureFlag.KmstackLeaveAndAbsenceTracking,
    FeatureFlag.KmstackABPayslip,
    FeatureFlag.KmstackABPayroll,
  ],
  [DashboardViewTabs.PAYMENTS]: [
    FeatureFlag.KmstackABInvoicePayment,
    FeatureFlag.KmstackABPartialPayment,
    FeatureFlag.KmstackABPaymentRequest,
    FeatureFlag.KmstackBulkPayments,
  ],
  [DashboardViewTabs.PURCHASES]: [FeatureFlag.KmstackABExpense, FeatureFlag.KmstackBillTracking],
  [DashboardViewTabs.INVENTORY]: [FeatureFlag.KmstackABInventory],
  [DashboardViewTabs.REPORT_ANALYTICS]: [
    FeatureFlag.KmstackABVATReport,
    FeatureFlag.KmstackABTaxFiling,
    FeatureFlag.KmstackABTaxReport,
    FeatureFlag.KmstackReportingAdvanced,
    FeatureFlag.KmstackABFinancialReport,
    FeatureFlag.KmstackDataExport,
  ],
  [DashboardViewTabs.AUTOMATION]: [
    FeatureFlag.KmstackABPaymentReminder,
    FeatureFlag.KmstackABInvoiceCategoryPrediction,
    FeatureFlag.KmstackAIForecasting,
    FeatureFlag.KmstackSmartContracts,
  ],
  [DashboardViewTabs.SECURITY_COMPLIANCE]: [
    FeatureFlag.KmstackDataPrivacyCompliance,
    FeatureFlag.KmstackAuditLogs,
    FeatureFlag.KmstackABFraudAlert,
  ],
  [DashboardViewTabs.INTEGRATIONS]: [
    FeatureFlag.KmstackAPIManagement,
    FeatureFlag.KmstackWebhookConfig,
    FeatureFlag.KmstackIntegrationLogs,
    FeatureFlag.KmstackPluginManagement,
  ],
  [DashboardViewTabs.SETTINGS]: [
    FeatureFlag.KmstackInvoiceTemplates,
    FeatureFlag.KmstackNotificationSystem,
    FeatureFlag.KmstackAutoTax,
    FeatureFlag.KmstackOrgSettings,
    FeatureFlag.KmstackUserManagement,
    FeatureFlag.KmstackRolePermissionManagement,
    FeatureFlag.KmstackAppSettingsManagement,
  ],
  [DashboardViewTabs.EMPLOYEE_PORTAL]: [
    FeatureFlag.KmstackEmployeePayslipPortal,
  ],
};

const linkFeatureMap: Record<string, readonly FeatureFlagType[]> = {
  '/org/payments/tracking': [FeatureFlag.KmstackABInvoicePayment],
  '/org/payments/crypto': [FeatureFlag.KmstackABCryptoPayment, FeatureFlag.KmstackABCryptoWallet],
  '/org/payments/partial-bnpl': [FeatureFlag.KmstackABPartialPayment],
  '/org/purchases/expenses': [FeatureFlag.KmstackABExpense],
  '/org/purchases/bills': [FeatureFlag.KmstackBillTracking],
  '/org/purchases/reports': [FeatureFlag.KmstackReportingAdvanced, FeatureFlag.KmstackDataExport],
  '/org/inventory/management': [FeatureFlag.KmstackABInventory],
  '/org/inventory/purchase-orders': [FeatureFlag.KmstackBillTracking],
  '/org/inventory/reports': [FeatureFlag.KmstackReportingAdvanced, FeatureFlag.KmstackDataExport],
  '/org/report-analytics/reports': [
    FeatureFlag.KmstackABVATReport,
    FeatureFlag.KmstackABTaxFiling,
    FeatureFlag.KmstackABTaxReport,
    FeatureFlag.KmstackReportingAdvanced,
  ],
  '/org/report-analytics/reports/financial-tax': [
    FeatureFlag.KmstackABVATReport,
    FeatureFlag.KmstackABTaxFiling,
    FeatureFlag.KmstackABTaxReport,
    FeatureFlag.KmstackABFinancialReport,
    FeatureFlag.KmstackABChartOfAccounts,
    FeatureFlag.KmstackABGeneralLedger,
  ],
  '/org/report-analytics/reports/operations': [FeatureFlag.KmstackReportingAdvanced],
  '/org/report-analytics/reports/analytics-forecasting': [FeatureFlag.KmstackAIForecasting],
  '/org/automation/smart-payment-reminders': [FeatureFlag.KmstackABPaymentReminder],
  '/org/automation/auto-categorization': [FeatureFlag.KmstackABInvoiceCategoryPrediction],
  '/org/automation/tax-reports': [FeatureFlag.KmstackABVATReport, FeatureFlag.KmstackABTaxFiling, FeatureFlag.KmstackABTaxReport],
  '/org/settings/invoice-settings': [FeatureFlag.KmstackInvoiceTemplates],
  '/org/settings/payment-methods': [FeatureFlag.KmstackABInvoicePayment, FeatureFlag.KmstackABPartialPayment],
  '/org/settings/notification': [FeatureFlag.KmstackNotificationSystem],
  '/org/settings/user-roles-permissions': [FeatureFlag.KmstackUserManagement, FeatureFlag.KmstackRolePermissionManagement, FeatureFlag.KmstackCustomRoles],
  '/org/settings/transaction-settings': [FeatureFlag.KmstackAutoTax, FeatureFlag.KmstackABChartOfAccounts],
  '/org/integrations/webhooks': [FeatureFlag.KmstackWebhookConfig],
  '/org/integrations/api': [FeatureFlag.KmstackAPIManagement],
  '/org/integrations/marketplace': [FeatureFlag.KmstackPluginManagement],
  '/org/security-compliance/dashboard': [FeatureFlag.KmstackABFraudAlert, FeatureFlag.KmstackAuditLogs],
  '/org/security-compliance/data-security': [FeatureFlag.KmstackDataPrivacyCompliance, FeatureFlag.KmstackAuditLogs],
  '/org/security-compliance/payment-security': [FeatureFlag.KmstackABFraudAlert],
  '/org/security-compliance/compliance-privacy': [FeatureFlag.KmstackDataPrivacyCompliance],
  '/org/workforce/users': [FeatureFlag.KmstackEmployeeManagement],
  '/org/workforce/time-tracking': [FeatureFlag.KmstackABTimesheet],
  '/org/workforce/time-tracking/timer': [FeatureFlag.KmstackABTimesheet],
  '/org/workforce/time-tracking/projects': [FeatureFlag.KmstackABTimesheet],
  '/org/workforce/time-tracking/time-sheets': [FeatureFlag.KmstackABTimesheet],
  '/org/workforce/time-tracking/invoicing': [FeatureFlag.KmstackABTimesheet],
  '/org/workforce/leave-approvals': [FeatureFlag.KmstackLeaveAndAbsenceTracking],
  '/org/workforce/payroll-processing': [FeatureFlag.KmstackABPayroll],
  '/org/workforce/payslips': [FeatureFlag.KmstackABPayslip],
  '/org/workforce/payroll-settings': [FeatureFlag.KmstackABPayroll],
};

export const featureFlagsForLink = (
  link: Pick<NavLink, 'href' | 'name' | 'viewId'>
): readonly FeatureFlagType[] => {
  const href = link.href?.startsWith('/') ? link.href : `/${ link.href }`;
  return linkFeatureMap[href] ?? featureFlagsForDashboardView(link.viewId);
};

export const featureFlagsForDashboardView = (
  viewId?: NavLink['viewId']
): readonly FeatureFlagType[] => {
  if (!viewId) return [];
  return dashboardViewFeatures[viewId as DashboardViewTabs] ?? [];
};

export const findCurrentLink = (pathname: string, iconClasses: string = "") => {
  if (pathname === '/org/workforce' || pathname.startsWith('/org/workforce/')) {
    return {
      name: 'Workforce',
      icon: <EmployeesIcon className={iconClasses} />,
      href: 'org/workforce',
      viewId: DashboardViewTabs.WORKFORCE,
    };
  }

  const combinedLinks = [
    ...dashBoardEssentialsLinks(iconClasses),
    ...employeePortalLinks(iconClasses),
    ...workforceSidebarLinks(iconClasses),
    ...payrollSidebarLinks(iconClasses),
    ...dashBoardToolsLinks(iconClasses)
  ];

  return combinedLinks
    .filter(link => {
      const href = link.href.startsWith('/') ? link.href : `/${ link.href }`;
      return pathname.startsWith(href);
    })
    .sort((a, b) => b.href.length - a.href.length)[0];
}

export const getInitialViewFromUrl = (pathname: string): any => {
  const currentLink = findCurrentLink(pathname);
  return currentLink?.viewId || 'overview';
}


export const timeTrackingTabs = (): NavLinkWithoutIcon[] => {
  return [
    { name: 'Timer', href: '/org/workforce/time-tracking/timer', viewId: 'timer' },
    { name: 'Projects', href: '/org/workforce/time-tracking/projects', viewId: 'projects' },
    { name: 'Time Sheets', href: '/org/workforce/time-tracking/time-sheets', viewId: 'time-sheets' },
    { name: 'Invoicing', href: '/org/workforce/time-tracking/invoicing', viewId: 'invoicing' },
  ]
}


export const workforceSubTabs = (): NavLinkWithoutIcon[] => {
  return [
    { name: 'Employees', href: '/org/workforce/users', viewId: 'users' },
    { name: 'Time Tracking', href: '/org/workforce/time-tracking', viewId: 'time-tracking' },
    { name: 'Leave Management', href: '/org/workforce/leave-approvals', viewId: 'leave-approvals' },
    { name: 'Payroll Processing', href: '/org/workforce/payroll-processing', viewId: 'payroll-processing' },
    { name: 'Payslips', href: '/org/workforce/payslips', viewId: 'payslips' },
    { name: 'Payroll Settings', href: '/org/workforce/payroll-settings', viewId: 'payroll-settings' }
  ];
}

export const automationSubTabs = (): NavLinkWithoutIcon[] => {
  return [
    { name: 'Smart Payment Reminders', href: '/org/automation/smart-payment-reminders', viewId: 'smart-payment-reminders' },
    { name: 'Auto-Categorization', href: '/org/automation/auto-categorization', viewId: 'auto-categorization' },
    { name: 'Tax Reports', href: '/org/automation/tax-reports', viewId: 'tax-reports' }
  ];
}

export const clientSupplierSubTabs = (): NavLinkWithoutIcon[] => {
  return [
    { name: 'Clients', href: '/org/clients-suppliers?tab=Clients', viewId: 'client' },
    { name: 'Suppliers', href: '/org/clients-suppliers?tab=Suppliers', viewId: 'supplier' },
    // { name: 'Price Lists', href: '/org/clients-suppliers/price-lists', viewId: 'price-lists' },
    // { name: 'Credit Notes', href: '/org/clients-suppliers/credit-notes', viewId: 'credit-notes' }
  ];
}


export const settingSubTabs = (): NavLinkWithoutIcon[] => {
  return [
    { name: 'Invoice Settings', href: '/org/settings/invoice-settings', viewId: 'invoice-settings' },
    { name: 'Payment Methods', href: '/org/settings/payment-methods', viewId: 'payment-methods' },
    { name: 'Notification', href: '/org/settings/notification', viewId: 'notification' },
    { name: 'User Roles & Permissions', href: '/org/settings/user-roles-permissions', viewId: 'user-roles-permissions' },
    { name: 'Transaction Settings', href: '/org/settings/transaction-settings', viewId: 'transaction-settings' },
  ]
}

export const integrationsSubTabs = (): NavLinkWithoutIcon[] => {
  return [
    { name: 'Webhooks', href: '/org/integrations/webhooks', viewId: 'webhooks' },
    { name: 'API', href: '/org/integrations/api', viewId: 'api' },
    { name: 'Marketplace', href: '/org/integrations/marketplace', viewId: 'marketplace' },
  ]
}


export const securityComplianceSubTabs = (): NavLinkWithoutIcon[] => {
  return [
    { name: 'Dashboard', href: '/org/security-compliance/dashboard', viewId: 'dashboard' },
    { name: 'Data Security', href: '/org/security-compliance/data-security', viewId: 'data-security' },
    { name: 'Payment Security', href: '/org/security-compliance/payment-security', viewId: 'payment-security' },
    { name: 'Compliance & Privacy', href: '/org/security-compliance/compliance-privacy', viewId: 'compliance-privacy' },
  ]
}

export const purchasesSubTabs = (): NavLinkWithoutIcon[] => {
  return [
    { name: 'Expenses', href: '/org/purchases/expenses', viewId: 'expenses' },
    { name: 'Bills', href: '/org/purchases/bills', viewId: 'bills' },
    { name: 'Reports', href: '/org/purchases/reports', viewId: 'reports' }
  ]
}

export const inventorySubTabs = (): NavLinkWithoutIcon[] => {
  return [
    { name: 'Inventory management', href: '/org/inventory/management', viewId: 'management' },
    { name: 'Purchase orders', href: '/org/inventory/purchase-orders', viewId: 'purchase-orders' },
    { name: 'Inventory reports', href: '/org/inventory/reports', viewId: 'reports' },
  ]
}

export const inventoryReportSubTabs = (): NavLinkWithoutHrefNIcon[] => {
  return [
    { name: 'Stock valuation', /*href: '/org/inventory/stock-valuation',*/ viewId: 'stock-valuation' },
    { name: 'Category breakdown', /*href: '/org/inventory/category-breakdown',*/ viewId: 'category-breakdown' },
    { name: 'Stock levels', /*href: '/org/inventory/stock-levels',*/ viewId: 'stock-levels' },
    { name: 'Products vs services', /*href: '/org/inventory/products-vs-services',*/ viewId: 'products-vs-services' },
    { name: 'Turnover rate', /*href: '/org/inventory/turnover-rate',*/ viewId: 'turnover-rate' },
    { name: 'Stock age', /*href:'/org/inventory/stock-age',*/ viewId: 'stock-age' },
    { name: 'Reorder analysis', /*href: '/org/inventory/reorder-analysis',*/ viewId: 'reorder-analysis' },
    { name: 'Stock-out risk', /*href: '/org/inventory/stockout-risk',*/ viewId: 'stockout-risk' },
  ]
}

export const inventoryManagementSubTabs = (): NavLinkWithoutHrefNIcon[] => {
  return [
    { name: 'Product', /*href: '/org/inventory/management/products',*/ viewId: 'products' },
    { name: 'Services', /*href: '/org/inventory/management/services',*/ viewId: 'services' },
  ]
}

export const payrollProcessingSubTabs = (): NavLinkWithoutHrefNIcon[] => {
  return [
    { name: 'Upcoming Payrolls', /*href: '/org/payments/upcoming-payrolls',*/ viewId: EPayrollProcessingSubTab.UPCOMING_PAYROLLS },
    { name: 'Past Payrolls', /*href: '/org/payments/past-payrolls',*/ viewId: EPayrollProcessingSubTab.PAST_PAYROLLS },
    { name: EPayrollProcessingSubTab.PAYSLIP_ISSUES, /*href: '/org/payments/past-payrolls',*/ viewId: EPayrollProcessingSubTab.PAYSLIP_ISSUES },
  ]
}

export const paymentViewSubTabs = (): NavLinkWithoutIcon[] => {
  return [
    { name: 'Tracking', href: '/org/payments/tracking', viewId: 'upcoming-payrolls' },
    { name: 'Crypto', href: '/org/payments/crypto', viewId: 'crypto' },
  ]
}

// export const tabStyleClass = "bg-transparent !p-0 gap-3 [&_button]:!rounded-full [&_button]:border [&_button]:px-5 [&_button]:py-2 [&_button]:text-[11px] [&_button]:font-black [&_button]:uppercase [&_button]:tracking-wider [&_button.bg-white]:!bg-primaryGreen [&_button.bg-white]:!text-white [&_button.bg-white]:border-primaryGreen [&_button.bg-white]:shadow-lg [&_button.bg-white]:shadow-primaryGreen/20 [&_button.text-gray-500]:bg-white [&_button.text-gray-500]:border-gray-200 [&_button.text-gray-400]:bg-white [&_button.text-gray-400]:border-gray-200 [&_button.text-gray-500]:hover:border-primaryGreen/30 [&_button.text-gray-500]:hover:text-primaryGreen ";
export const tabStyleClass = "bg-transparent text-xs !p-0 gap-2 [&_button]:!rounded-lg [&_button]:border [&_button]:font-semibold [&_button]:tracking-wide [&_button.bg-white]:!bg-primaryGreen [&_button.bg-white]:!text-white [&_button.bg-white]:border-primaryGreen [&_button.text-gray-500]:bg-white [&_button.text-gray-500]:border-gray-200 [&_button.text-gray-400]:bg-white [&_button.text-gray-500]:hover:text-primaryGreen";

export type FeatureGatedItem = {
  features?: readonly FeatureFlagType[];
};

export const canAccessFeatureGatedItem = (
  item: FeatureGatedItem,
  plan: PlanType,
  role?: RoleType | string,
) => {
  return !item.features?.length || hasAnyFeature(plan, item.features, role as RoleType | undefined);
};

export const filterFeatureGatedItems = <T extends FeatureGatedItem>(
  items: readonly T[],
  plan: PlanType,
  role?: RoleType | string,
) => items.filter(item => canAccessFeatureGatedItem(item, plan, role));
